import { useState, useEffect } from 'react';
import { getCompletedMilestones } from '@network/Questions';
import { ITier, ITierMilestone } from '@network/Tier';

export const useMilestoneCompletion = () => {
  const [completedMilestones, setCompletedMilestones] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompletedMilestones = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCompletedMilestones();
      setCompletedMilestones(response.completedMilestones || []);
    } catch (err: any) {
      console.error('Error fetching completed milestones:', err);
      setError(err?.message || 'Failed to fetch completed milestones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletedMilestones();
  }, []);

  const isMilestoneCompleted = (milestoneId: number) => {
    return completedMilestones.includes(milestoneId);
  };

  const isMilestoneAvailable = (milestoneId: number, tiers: ITier[]) => {
    // Find which tier this milestone belongs to
    const tierWithMilestone = tiers.find(tier => 
      tier.tierMilestones?.some(tm => tm.milestone.id === milestoneId)
    );

    if (!tierWithMilestone) return false;

    // Get all tiers sorted by order
    const sortedTiers = tiers.sort((a, b) => a.order - b.order);
    const currentTierIndex = sortedTiers.findIndex(tier => tier.id === tierWithMilestone.id);

    // Get all milestones in this tier, sorted by order
    const tierMilestones = tierWithMilestone.tierMilestones || [];
    const sortedMilestones = tierMilestones.sort((a, b) => a.order - b.order);

    // Find the index of current milestone
    const currentMilestoneIndex = sortedMilestones.findIndex(tm => tm.milestone.id === milestoneId);
    
    if (currentMilestoneIndex === -1) return false;

    // If it's the first milestone in the first tier, it's always available
    if (currentTierIndex === 0 && currentMilestoneIndex === 0) return true;

    // If it's the first milestone in any tier (not the first tier), 
    // check if all previous tiers are completed
    if (currentMilestoneIndex === 0 && currentTierIndex > 0) {
      for (let i = 0; i < currentTierIndex; i++) {
        const previousTier = sortedTiers[i];
        const previousTierMilestones = previousTier.tierMilestones || [];
        const allPreviousTierMilestonesCompleted = previousTierMilestones.every(tm => 
          isMilestoneCompleted(tm.milestone.id)
        );
        if (!allPreviousTierMilestonesCompleted) {
          return false;
        }
      }
      return true;
    }

    // If it's not the first milestone in the tier, check if all previous milestones 
    // in this tier are completed
    for (let i = 0; i < currentMilestoneIndex; i++) {
      const previousMilestone = sortedMilestones[i];
      if (!isMilestoneCompleted(previousMilestone.milestone.id)) {
        return false;
      }
    }

    return true;
  };

  const getTierProgress = (tier: ITier) => {
    const tierMilestones = tier.tierMilestones || [];
    const completedCount = tierMilestones.filter(tm => 
      isMilestoneCompleted(tm.milestone.id)
    ).length;
    
    return {
      completed: completedCount,
      total: tierMilestones.length,
      percentage: tierMilestones.length > 0 ? (completedCount / tierMilestones.length) * 100 : 0,
      isCompleted: completedCount === tierMilestones.length
    };
  };

  const refreshCompletionStatus = () => {
    fetchCompletedMilestones();
  };

  return {
    completedMilestones,
    loading,
    error,
    isMilestoneCompleted,
    isMilestoneAvailable,
    getTierProgress,
    refreshCompletionStatus,
  };
};
